import Vue from "vue";
import Vuex from "vuex";
import { createStore } from "vuex-extensions";
import createPersistedState from "vuex-persistedstate";
import mediaModule from "./media";
import devicesModule from "./devices";
import userModule from "./user";
import playlistsModule from "./playlists";
import User from "@/classes/User";
import config from '../../app.config';


/* 
NEW GLOBAL CONFIG FILE SEE ../../app.config.js

Backend
// https://api-dev.mirrads.de
// https://api-q.mirrads.de
// https://api.mirrads.de

const backend = {
	url: "https://api-dev.mirrads.de", // On migration only change this url
	devUrl: "https://api-dev.mirrads.de", // Do not change
	qualityUrl: "https://api-q.mirrads.de", // Do not change
	liveClusterA: "https://api.mirrads.de", // Do not change
	liveClusterB: "https://api-b.mirrads.de" // Do not change
};

const version = '0.7.1' // Update on migration
const hostName = "https://cms-dev.mirrads.de"; // On migration only change this url
*/
Vue.use(Vuex);

export interface GlobalInterface {
	showFirstUseTour: boolean;
	serverUrl: string;
	api: string;
	backendDevUrl: string;
	updateUrl: string;
	backendQualityUrl: string;
	liveClusterA: string;
	liveClusterB: string;
	hostname: string;
	version: string;
	token: string;
	user: User;
	tokenExpiresInMs: string;
	fbToken: string;
}

function getDefaultState(): GlobalInterface {
	return {
		showFirstUseTour: false,
		serverUrl: config.backend.url + "/",
		api: config.backend.url + "/api/",
		updateUrl: config.update.url,
		backendDevUrl: config.backend.devUrl,
		backendQualityUrl: config.backend.qualityUrl,
		liveClusterA: config.backend.liveClusterA,
		liveClusterB: config.backend.liveClusterB,
		hostname: config.frontend.url,
		version: config.frontend.version,
		token: "",
		user: User.emptyUser(),
		tokenExpiresInMs: "30000",
		fbToken: "",
	};
}

const globalState = getDefaultState();

export default createStore(Vuex.Store, {
	modules: {
		mediaModule,
		devicesModule,
		userModule,
		playlistsModule,
	},
	strict: true,
	plugins: [createPersistedState()],
	state: globalState,
	mutations: {
		RESET: (state) => {
			Object.assign(globalState, getDefaultState());
			state.token = "";
		},
		// they must be here because the mutate the global interface
		setFBToken: (state, token: string) => {
			state.fbToken = token;
		},
		setToken: (state, token: string) => {
			state.token = token;
		},
		setUser: (state, user: User) => {
			state.user = user;
		},
		setUserSettings: (state, payload: { isNotificationReceiver: boolean }) => {
			state.user.isNotificationReceiver = payload.isNotificationReceiver;
		},
		setUserFeatureList(state, userFeatureList) {
			if (state.user) {
				state.user.setFeatureList(userFeatureList);
			}
		},
		setUserEmail: (state, mail: string) => {
			state.user.email = mail;
		},
		setTokenExpire: (state, expiresInMs: string) => {
			state.tokenExpiresInMs = expiresInMs;
		},
		updateFirstUseState: (state, showFirstUse: boolean) =>
			(state.showFirstUseTour = showFirstUse),
	},
	getters: {
		getApi: (state) => state.api,
		getHost: (state) => state.serverUrl,
		getHostname: (state) => state.hostname,
		getBackendDevUrl: (state) => state.backendDevUrl,
		getBackendQualityUrl: (state) => state.backendQualityUrl,
		getBackendLiveClusterAUrl: (state) => state.liveClusterA,
		getBackendLiveClusterBUrl: (state) => state.liveClusterB,
		getVersion: (state) => state.version,
		getFirstUseState: (state) => state.showFirstUseTour,
		isLoggedIn: (state) => {
			return state.token;
		},
		getUser: (state) => {
			if (state.user instanceof User) {
				return state.user;
			} else {
				return User.fromObject(state.user);
			}
		},
		getTokenExpireInMs: (state) => {
			return state.tokenExpiresInMs;
		},
		getFbToken: (state) => {
			return state.fbToken;
		},
	},
});
